/**
 * Got tired of a try/catch around this feature everywhere.
 *
 * It has to look like a JSON string or no attempt is made to parse it on the
 * theory it is better to NOT throw an error if it can be prevented.
 *
 * @param jsonString
 * @returns
 */

import { STRING_BOOLEAN_HASH } from '../constants/globals';
import { hasProperty } from './hasProperty';
import { autoMinifyJson, expandJson, isMinified, minifyJson } from './jsonMinify';
// jsonpack dependency removed - using direct JSON with obliterate instead

// Just a shortcut to compare if no options are needed.
function diff(obj1: any, obj2: any): any {
  return compare({ obj1: { value: obj1 }, obj2: { value: obj2 } });
}

type CompareForSqlUpdateSchema = {
  /**
   * If set to 'force', this field will always be included in the UPDATE statement
   * regardless of whether its value changed.  Use the optional `value` property to
   * provide the literal that should be bound to the query parameters.
   */
  type?: 'force';
  /** optional literal value to force-set */
  value?: any;
  /**
   * When present and `false`, the field is ignored even if different.
   * Omit or set to `true` to allow normal diff behaviour.
   */
  enabled?: boolean;
};

type CompareForSqlUpdateOptions = {
  tableName: string;
  pkColumn: string;
  schema: Record<string, boolean | CompareForSqlUpdateSchema>;
};

/**
 * Compare two objects and generate a SQL UPDATE statement for changed fields.
 * This handles a very-specific use case and my not be appropriate for everything.
 * Make sure you verify the SQL!!!
 *
 * @param beforeObj The original object (before changes)
 * @param afterObj The updated object (after changes)
 * @param options Options for the comparison
 * @returns { update: string, params: Record<string, any> } | null
 */
function compareForSqlUpdate(
  beforeObj: any,
  afterObj: any,
  options: CompareForSqlUpdateOptions
): {
  id: any;
  update: string;
  params: Record<string, any>;
  skips: Array<{ field: string; value: any; previousValue: any }>;
} | null {
  // Deep equality helper
  function isDeepEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (typeof a !== typeof b) return false;

    if (a && b && typeof a === 'object') {
      // Both arrays
      if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;

        for (let i = 0; i < a.length; i++) {
          if (!isDeepEqual(a[i], b[i])) return false;
        }

        return true;
      }

      // Both objects
      const aKeys = Object.keys(a);
      const bKeys = Object.keys(b);
      if (aKeys.length !== bKeys.length) return false;

      for (const key of aKeys) {
        if (!isDeepEqual(a[key], b[key])) return false;
      }

      return true;
    }

    return false;
  }

  const changed: Record<string, any> = {};
  const { tableName, pkColumn, schema } = options;
  // Only include fields present in schema and enabled
  const schemaKeys = Object.keys(schema || {});
  const skips: Array<{ field: string; value: any; previousValue: any }> = [];
  const keys = schemaKeys.filter(key => {
    const val = schema[key] as CompareForSqlUpdateSchema;
    if (typeof val === 'boolean') return val;

    if (typeof val === 'object' && val !== null) {
      if (val.enabled === false) {
        // If field is disabled, record a skip if it would have been updated
        if (!isDeepEqual(beforeObj[key], afterObj[key])) {
          skips.push({
            field: key,
            value: afterObj[key],
            previousValue: beforeObj[key],
          });
        }

        return false;
      }

      return true;
    }

    return false;
  });

  for (const key of keys) {
    const schemaVal = schema[key] as CompareForSqlUpdateSchema | boolean;

    if (typeof schemaVal === 'object' && schemaVal?.type === 'force') {
      // Forced field – always update to provided literal (or afterVal fallback)
      changed[key] = 'value' in schemaVal ? schemaVal.value : afterObj[key];

      continue;
    }

    const beforeVal = beforeObj[key];
    const afterVal = afterObj[key];

    // Only include fields that changed (deep equality)
    if (!isDeepEqual(beforeVal, afterVal)) {
      changed[key] = afterVal;
    }
  }

  if (Object.keys(changed).length === 0) return null;

  changed[pkColumn] = afterObj[pkColumn];

  const setClause = Object.keys(changed)
    .filter(key => key !== pkColumn)
    .map(key => `${key} = :${key}`)
    .join(', ');

  const sql = `UPDATE ${tableName} SET ${setClause} WHERE ${pkColumn} = :${pkColumn}`;

  return {
    id: afterObj[pkColumn],
    update: sql,
    params: changed,
    skips,
  };
}

// Written by CoPilot.
function compare({
  obj1,
  obj2,
  options = {
    ignoreLeafKey: {},
    compareLeafAs: {},
    strict: false,
    traceKey: {},
  },
}: {
  obj1: { name?: string; value: any };
  obj2: { name?: string; value: any };
  options?: {
    ignoreLeafKey?: STRING_BOOLEAN_HASH;
    compareLeafAs?: { [key: string]: 'string' | 'number' | 'boolean' };
    strict?: boolean;
    traceKey?: STRING_BOOLEAN_HASH;
  };
}): any {
  const diffs: any = {},
    obj1Name = obj1.name ?? 'obj1',
    obj2Name = obj2.name ?? 'obj2',
    strict = options?.strict;

  function findDiffs(o1: any, o2: any, path: string = '') {
    if (!o1 || !o2) return { error: 'missing object' };

    for (const key in o1) {
      const o1AsAny = o1 as any,
        value = o1AsAny[key] ?? null;

      if (options?.traceKey?.[key]) {
        console.log({ loop: 'key in o1', key, path });
      }

      if (!(key in o2)) {
        if (options.traceKey?.[key]) {
          console.log({
            loop: 'key in o1; key NOT in o2',
            strict,
            key,
            path,
            typeofObj: typeof o1AsAny,
            value,
            typeofValue: typeof value,
            test: !strict && value === null,
          });
        }

        if (options?.ignoreLeafKey?.[key]) continue;

        if (!strict && (o1AsAny[key] === null || o1AsAny[key] === undefined)) {
          continue;
        }

        diffs[path + key] = {
          type: `missing_in_${obj2Name}`,
          value: o1[key],
        };
      } else if (typeof o1[key] === 'object' && typeof o2[key] === 'object') {
        findDiffs(o1[key], o2[key], path + key + '.');
      } else if (o1[key] !== o2[key]) {
        if (options?.ignoreLeafKey?.[key]) continue;

        if (options?.compareLeafAs?.[key] === 'string') {
          if (String(o1[key]) === String(o2[key])) {
            continue;
          }
        } else if (options?.compareLeafAs?.[key] === 'number') {
          if (Number(o1[key]) === Number(o2[key])) {
            continue;
          }
        } else if (options?.compareLeafAs?.[key] === 'boolean') {
          if (Boolean(o1[key]) === Boolean(o2[key])) {
            continue;
          }
        }

        diffs[path + key] = {
          type: 'value_changed',
          value1: o1[key],
          value2: o2[key],
        };
      }
    }

    for (const key in o2) {
      const o2AsAny = o2 as any,
        value = o2AsAny[key] ?? null;

      if (options?.traceKey?.[key]) {
        console.log({ loop: 'key in o2', key, path, value });
      }

      if (!(key in o1)) {
        if (options?.ignoreLeafKey?.[key]) continue;

        if (options.traceKey?.[key]) {
          console.log({
            loop: 'key in o2; key NOT in o1',
            strict,
            key,
            path,
            // obj: o2AsAny,
            typeofObj: typeof o2AsAny,
            value,
            typeofValue: typeof value,
            test: !strict && value === null,
          });
        }

        if (!strict && value === null) continue;
        diffs[path + key] = {
          type: `missing_in_${obj1Name}`,
          value: o2[key],
        };
      } else {
        if (options?.traceKey?.[key]) {
          console.log({
            loop: 'key in o2; key in o1',
            key,
            path,
          });
        }
      }
    }
  }

  findDiffs(obj1.value, obj2.value);

  return diffs;
}

/**
 * Parse a JSON string without dying and provide an optional default if it fails.
 *
 * When `options.permissive` is true, the function:
 * - Accepts bare JSON primitives ("5", "true", '"hello"') instead of requiring { or [
 * - Preserves falsy defaults (false, 0, "") instead of coercing them to null
 * - Returns defaultValue (not the raw string) on parse errors, silently
 * - Falls back to defaultValue when JSON.parse produces null
 *
 * @param jsonString - The JSON string to parse.
 * @param def - The default value to return if the JSON string is not valid.
 * @param options - Optional options object.
 * @param options.permissive - When true, the function accepts bare JSON primitives instead of requiring { or [
 * @returns The parsed JSON object or the default value if the JSON string is not valid.
 */
const parse = (jsonString: any, def?: any, options?: { permissive?: boolean }) => {
  if (options?.permissive) {
    if (jsonString == null || jsonString === '') return def;

    try {
      const parsed = JSON.parse(jsonString);

      return parsed ?? def;
    } catch {
      return def;
    }
  }

  let resp = def !== undefined ? def : null;

  if (!jsonString) {
    return resp;
  }

  if (Array.isArray(jsonString)) {
    return jsonString;
  }

  if (typeof jsonString === 'object') {
    return jsonString;
  }

  if (typeof jsonString !== 'string') {
    return resp;
  }

  if (jsonString.indexOf('{') !== 0 && jsonString.indexOf('[') !== 0) {
    return resp;
  }

  try {
    resp = JSON.parse(jsonString);
  } catch (e) {
    console.error('Error parsing JSON string:', jsonString, e);
  }

  return resp;
};

/**
 * Stringify a JSON object without dying and provide an optional default if it fails.
 *
 * @param obj - The object to stringify.
 * @param def - The default value to return if the stringification fails. Defaults to an empty string.
 * @returns The stringified JSON object or the default value if the stringification fails.
 */
const stringify = (
  obj: any,
  def?: string | null,
  options?: { existingJsonGuard?: boolean; exactDefault?: boolean }
) => {
  const { existingJsonGuard = false, exactDefault = false } = options || {};

  if (existingJsonGuard && typeof obj === 'string') {
    // Guard for already-serialized JSON (common for MySQL JSON columns).
    // Validate the JSON string, but do NOT return a trimmed version.
    const check = obj.trim();

    // Treat empty/whitespace as "no document" for JSON columns.
    if (check === '') {
      return exactDefault ? def : def || '';
    }

    if (check.startsWith('{') || check.startsWith('[')) {
      try {
        JSON.parse(check);

        return obj; // return original string (no trimming)
      } catch {
        // Invalid JSON string — fall back to default to avoid downstream JSON-column errors.
        return exactDefault ? def : def || '';
      }
    }

    // For JSON columns: if it's not valid JSON text, don't wrap it in quotes.
    // Returning the default avoids persisting invalid/meaningless JSON.
    return exactDefault ? def : def || '';
  }

  let resp = exactDefault ? def : def || '';

  if (obj) {
    try {
      resp = JSON.stringify(obj);
    } catch (e) {
      console.error('Error stringifying JSON object:', obj, e);
    }
  }

  return resp;
};

/**
 * Stringify a JSON value for binding to a MySQL JSON column.
 *
 * Returns `null` for `null`/`undefined` inputs so the column gets SQL NULL,
 * and ALSO returns `null` when `stringify()` falls back to its empty-string
 * default (which happens for falsy non-null inputs like `false` or `0` due
 * to its internal `if (obj)` short-circuit). MySQL would reject `''` as
 * invalid JSON, so coalescing it back to NULL is the safe move.
 *
 * Use this whenever you're binding a value to a JSON column on insert/update.
 */
const toJsonOrNull = (v: unknown | null | undefined): string | null => {
  if (v === null || v === undefined) return null;
  const s = stringify(v);

  return s === '' || s === undefined ? null : s;
};

const pretty = (obj: any, def?: string | null) => {
  let resp = def || '';

  if (obj) {
    try {
      resp = JSON.stringify(obj, null, 2);
    } catch (e) {
      console.error('Error stringifying JSON object:', obj, e);
    }
  }

  return resp;
};

/**
 * Just seemed we needed this.
 *
 * @param obj
 * @returns
 */
const clone = (obj: any, def?: any) => {
  if (!obj) return def ? def : obj;
  if (typeof obj !== 'object') return def ? def : obj;
  let resp = def || null;

  try {
    resp = JSON.parse(JSON.stringify(obj));
  } catch (e) {
    console.error('Error cloning JSON object:', obj, e);
  }

  return resp;
};

/**
 * Just seemed we needed this.
 *
 * @param obj
 * @returns
 */
const cloneClean = (obj: any, def?: any) => {
  if (!obj) return def ? def : obj;
  if (typeof obj !== 'object') return def ? def : obj;
  let resp = def || null;

  try {
    resp = noNulls(JSON.parse(JSON.stringify(obj)));
  } catch (e) {
    console.error('Error cloning JSON object:', obj, e);
  }

  return resp;
};

/**
 * Remove nulls from an object or array (recursively).
 *
 * BEHAVIOR CHANGE (2026-01):
 * - OLD: Only worked on objects; arrays passed through unchanged.
 * - NEW: Properly recurses into arrays, removes nulls, preserves index alignment
 *        (arrays may become sparse). Empty arrays/objects return `[]`/`{}`.
 *
 * @param incoming - The object or array to remove nulls from.
 * @returns The object/array with nulls removed. Empty arrays return `[]`, empty objects return `{}`.
 */
const noNulls = (incoming: any) =>
  obliterate(incoming, {
    compactArrays: false, // Preserves index alignment (arrays may become sparse)
  });

/**
 * Obliterate an object or array (recursively). Removes all nulls recursively.
 *
 * @param incoming - The object or array to obliterate.
 * @param options - Control compaction behavior.
 * @returns The object/array with all nulls obliterated.
 *
 * BEHAVIOR BY MODE:
 * - `compactArrays: true`: Arrays are compacted (indexes shift down).
 *   Empty arrays/objects return `undefined` (completely removed from output).
 * - `compactArrays: false` (default): Arrays preserve index alignment (may become sparse).
 *   Empty arrays/objects return `[]`/`{}` (safe for `.reduce()`, `.map()`, etc.).
 */
type ObliterateOptions = {
  /**
   * When true, arrays are compacted (removed elements shift indexes).
   * When false, array length/index alignment is preserved (holes may remain).
   */
  compactArrays?: boolean;
};

const obliterate = (incoming: any, options: ObliterateOptions = { compactArrays: false }): any => {
  if (incoming === null || incoming === undefined) {
    return undefined;
  }

  if (Array.isArray(incoming)) {
    if (options.compactArrays) {
      // Compact arrays: remove undefined entries and shift indexes down.
      const compacted = incoming
        .map(item => obliterate(item, options))
        .filter(item => item !== undefined);

      // Historical `obliterate` behavior: drop empty arrays entirely.
      return compacted.length > 0 ? compacted : undefined;
    }

    // Preserve indexes/length (do NOT compact arrays).
    // This keeps index alignment for callers relying on positional arrays.
    const result: any[] = [];
    result.length = incoming.length;

    for (let i = 0; i < incoming.length; i++) {
      const cleanedValue = obliterate(incoming[i], options);

      if (cleanedValue !== undefined) {
        result[i] = cleanedValue;
      }
    }

    return result;
  }

  if (typeof incoming === 'object') {
    const result: Record<string, any> = {};

    for (const key in incoming) {
      if (Object.prototype.hasOwnProperty.call(incoming, key)) {
        const cleanedValue = obliterate(incoming[key], options);

        if (cleanedValue !== undefined) {
          result[key] = cleanedValue;
        }
      }
    }

    // Historical `obliterate` behavior: drop empty objects entirely.
    if (options.compactArrays && Object.keys(result).length === 0) {
      return undefined;
    }

    return result;
  }

  return incoming;
};

const deepEqual = (a: any, b: any): boolean => JSON.stringify(a) === JSON.stringify(b);

/**
 * Order-independent deep equality. Unlike `deepEqual` (which relies on
 * JSON.stringify and is therefore key-order-dependent), this compares
 * objects structurally: { a: 1, b: 2 } equals { b: 2, a: 1 }.
 */
const deepEqualAnyOrder = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== typeof b) return false;

  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;

    return a.every((val, i) => deepEqualAnyOrder(val, b[i]));
  }

  if (typeof a === 'object') {
    const aObj = a as Record<string, unknown>;
    const bObj = b as Record<string, unknown>;
    const aKeys = Object.keys(aObj);
    const bKeys = Object.keys(bObj);

    if (aKeys.length !== bKeys.length) return false;

    return aKeys.every(key => hasProperty(bObj, key) && deepEqualAnyOrder(aObj[key], bObj[key]));
  }

  return a === b;
};

/**
 * Just in case we need more functions on JSON objects that we use all the
 * time.
 */
export const ChiroUpJSON = {
  clone,
  cloneClean,
  compare,
  compareForSqlUpdate,
  diff,
  noNulls,
  obliterate,
  parse,
  pretty,
  stringify,
  toJsonOrNull,
  deepEqual,
  deepEqualAnyOrder,
  autoMinify: autoMinifyJson,
  expand: expandJson,
  isMinified,
  minify: minifyJson,
};
